import { useEffect, useState, Fragment } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { UserPlus, Mail, Copy, ShieldCheck, Hammer, X, Crown, RefreshCw, HardHat, Users2, CheckCircle2, AlertTriangle, MonitorSmartphone } from "lucide-react";
import { DeviceList } from "../components/DeviceList";
import { toast } from "sonner";
import { MemberMenu } from "../components/MemberMenu";
import { CraftsmenCards } from "../components/CraftsmenCards";
import { ROLE_LABELS, ROLE_TECH, isOwner as isOwnerRole } from "../lib/roles";

const ROLE_BADGE = {
    owner: "bg-orange-100 text-orange-700 border-orange-300",
    admin: "bg-blue-100 text-blue-700 border-blue-300",
    lead_technician: "bg-slate-200 text-slate-700 border-slate-300",
    craftsman: "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const ROLE_ICON = { owner: Crown, admin: ShieldCheck, lead_technician: Hammer, craftsman: Hammer };

export default function Team() {
    const { user, setUser } = useAuth();
    const [members, setMembers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [showInvite, setShowInvite] = useState(false);
    const tech = user?.role === ROLE_TECH;
    const [inviteForm, setInviteForm] = useState({ email: "", role: tech ? "craftsman" : ROLE_TECH, craftsman_id: "" });
    const [tab, setTab] = useState("members");
    const [devicesFor, setDevicesFor] = useState(null);
    const [cards, setCards] = useState([]);
    useEffect(() => { api.get("/craftsmen/cards").then((r) => setCards((r.data || []).filter((c) => c.is_active !== false && !c.user_id))).catch(() => {}); }, []);

    const isOwnerOrAdmin = user?.role === "owner" || user?.role === "admin";
    const canInvite = isOwnerOrAdmin || tech;
    const owner = isOwnerRole(user);

    const load = async () => {
        const [m, i] = await Promise.all([
            api.get("/team/members"),
            canInvite ? api.get("/team/invitations") : Promise.resolve({ data: [] }),
        ]);
        setMembers(m.data);
        setInvitations(i.data);
    };
    useEffect(() => { load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const invite = async () => {
        if (!inviteForm.email) { toast.error("Zadejte email"); return; }
        try {
            const { data } = await api.post("/team/invite", inviteForm);
            if (data.email_status === "sent") toast.success(`Pozvánka odeslána na ${data.email}`);
            else { toast.error(`Pozvánka vytvořena, ale e-mail se nepodařilo odeslat: ${data.email_error || "neznámá chyba"}. Zkopírujte odkaz a pošlete jej ručně.`, { duration: 9000 }); if (data.token) copyLink(data.token); }
            setShowInvite(false);
            setInviteForm({ email: "", role: tech ? "craftsman" : ROLE_TECH, craftsman_id: "" });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Pozvánka selhala");
        }
    };

    const changeInviteRole = async (id, role) => {
        try { await api.put(`/team/invitations/${id}/role`, { role }); toast.success("Role pozvánky změněna"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Změna selhala"); }
    };

    const resendInvite = async (id) => {
        try {
            const { data } = await api.post(`/team/invitations/${id}/resend`);
            if (data.email_status === "sent") toast.success(`Pozvánka znovu odeslána na ${data.email} (platí dalších 7 dní)`);
            else { copyLink(data.token); toast.error(`E-mail se nepodařilo odeslat: ${data.email_error || "neznámá chyba"}. Nový odkaz je ve schránce.`, { duration: 9000 }); }
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Obnovení selhalo"); }
    };

    const cancelInvite = async (id) => {
        await api.delete(`/team/invitations/${id}`);
        load();
    };

    const copyLink = (token) => {
        const link = `${window.location.origin}/pozvanka/${token}`;
        navigator.clipboard.writeText(link);
        toast.success("Odkaz zkopírován");
    };

    return (
        <div className="px-10 py-10 max-w-5xl mx-auto animate-fade-in" data-testid="team-page">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow mb-2">Tým</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900">Tým firmy</h1>
                    <p className="text-sm text-slate-600 mt-2">{owner ? "Pozvěte kolegy do firmy a přiřaďte jim role." : tech ? "Jako vedoucí technik můžete pozvat řemeslníka – po přijetí pozvánky si zapisuje vlastní hodiny a jízdy." : "Jako administrátor můžete pozvat vedoucího technika nebo řemeslníka."}</p>
                </div>
                {canInvite && tab === "members" && (
                    <button onClick={() => setShowInvite(true)} data-testid="invite-button"
                            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm">
                        <UserPlus className="w-4 h-4" /> {owner ? "Pozvat člena" : tech ? "Pozvat řemeslníka" : "Pozvat člena"}
                    </button>
                )}
            </div>

            <div className="border-b border-slate-200 mb-6 flex gap-1">
                {[{ k: "members", l: "Členové týmu", I: Users2 }, ...(tech ? [] : [{ k: "craftsmen", l: "Řemeslníci", I: HardHat }])].map((t) => (
                    <button key={t.k} onClick={() => setTab(t.k)} data-testid={`team-tab-${t.k}`}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-wider border-b-2 ${tab === t.k ? "border-orange-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
                        <t.I className="w-3.5 h-3.5" /> {t.l}
                    </button>
                ))}
            </div>

            {tab === "craftsmen" && <CraftsmenCards />}
            {tab === "members" && (<>

            {/* Members */}
            <div className="bg-white border border-slate-200 mb-8">
                <div className="px-6 py-3 border-b-2 border-slate-900 flex items-center justify-between">
                    <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Aktivní členové</div>
                    <span className="text-xs text-slate-500">{members.length}</span>
                </div>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                            <th className="px-6 py-3 font-semibold">Jméno</th>
                            <th className="px-6 py-3 font-semibold">Email</th>
                            <th className="px-6 py-3 font-semibold">Role</th>
                            <th className="px-6 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {members.map((m) => {
                            const Icon = ROLE_ICON[m.role] || Hammer;
                            return (
                                <Fragment key={m.id}>
                                <tr className={`border-b border-slate-100 ${!m.is_active ? "opacity-60 bg-slate-50" : ""}`} data-testid={`member-row-${m.id}`}>
                                    <td className="px-6 py-3 font-medium text-slate-900">
                                        {m.name || "—"}{m.id === user?.id && <span className="ml-2 text-xs text-orange-600">(vy)</span>}
                                        {!m.is_active && <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-slate-400 bg-slate-200 text-slate-600" data-testid={`inactive-badge-${m.id}`}>Deaktivován</span>}
                                        {m.phone && <div className="text-xs text-slate-500 font-normal">{m.phone}</div>}
                                    </td>
                                    <td className="px-6 py-3 text-slate-600">{m.email}</td>
                                    <td className="px-6 py-3">
                                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${ROLE_BADGE[m.role] || ROLE_BADGE.lead_technician}`} data-testid={`role-badge-${m.id}`}>
                                            <Icon className="w-3 h-3" />
                                            {ROLE_LABELS[m.role] || m.role}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 text-right whitespace-nowrap">
                                        {owner && <button onClick={() => setDevicesFor(devicesFor === m.id ? null : m.id)} className="mr-1 p-2 text-slate-400 hover:text-slate-900" title="Zařízení" data-testid={`member-devices-${m.id}`}><MonitorSmartphone className="w-4 h-4" /></button>}
                                        {isOwnerOrAdmin && <MemberMenu member={m} me={user} onChanged={async () => { await load(); if (m.id === user?.id || user?.role === "owner") api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {}); }} />}
                                    </td>
                                </tr>
                                {devicesFor === m.id && (
                                    <tr className="border-b border-slate-200 bg-slate-50/60"><td colSpan={4} className="p-0"><DeviceList memberId={m.id} /></td></tr>
                                )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pending invitations */}
            {isOwnerOrAdmin && (
                <div className="bg-white border border-slate-200">
                    <div className="px-6 py-3 border-b-2 border-slate-900 flex items-center justify-between">
                        <div className="font-display font-bold text-slate-900 uppercase tracking-wider text-sm">Čekající pozvánky</div>
                        <span className="text-xs text-slate-500">{invitations.length}</span>
                    </div>
                    {invitations.length === 0 ? (
                        <div className="p-8 text-center text-slate-500 text-sm">Žádné čekající pozvánky.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                                    <th className="px-6 py-3 font-semibold">Email</th>
                                    <th className="px-6 py-3 font-semibold">Role</th>
                                    <th className="px-6 py-3 font-semibold">Vytvořeno</th>
                                    <th className="px-6 py-3 font-semibold">E-mail</th>
                                    <th className="px-6 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {invitations.map((inv) => (
                                    <tr key={inv.id} className={`border-b border-slate-100 ${inv.expired ? "bg-red-50/50" : ""}`} data-testid={`invite-row-${inv.id}`}>
                                        <td className="px-6 py-3 text-slate-700 flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-400" />{inv.email}</td>
                                        <td className="px-6 py-3">
                                            {owner ? (
                                                <select value={inv.role} onChange={(e) => changeInviteRole(inv.id, e.target.value)} data-testid={`invite-role-${inv.id}`}
                                                        className="px-2 py-1 border border-slate-300 text-xs rounded-sm bg-white">
                                                    <option value={ROLE_TECH}>Vedoucí technik</option>
                                                    <option value="admin">Administrátor</option>
                                                </select>
                                            ) : <span className="text-xs text-slate-700">{ROLE_LABELS[inv.role] || inv.role}</span>}
                                        </td>
                                        <td className="px-6 py-3 text-xs text-slate-500">
                                            {new Date(inv.created_at).toLocaleDateString("cs-CZ")}
                                            <div className={inv.expired ? "text-red-700 font-semibold" : "text-slate-400"} data-testid={`invite-expiry-${inv.id}`}>
                                                {inv.expired ? "Vypršela" : `platí do ${new Date(inv.expires_at).toLocaleDateString("cs-CZ")}`}
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 text-xs" data-testid={`invite-email-status-${inv.id}`}>
                                            {inv.email_status === "delivered" ? <span className="inline-flex items-center gap-1 font-semibold text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Doručeno</span>
                                                : inv.email_status === "sent" ? <span className="inline-flex items-center gap-1 font-semibold text-sky-700"><Mail className="w-3.5 h-3.5" /> Odesláno</span>
                                                : inv.email_status === "error" ? <div className="text-red-700"><span className="inline-flex items-center gap-1 font-semibold"><AlertTriangle className="w-3.5 h-3.5" /> Chyba</span><div className="text-[11px] max-w-[220px] truncate" title={inv.email_error}>{inv.email_error}</div>{inv.token && <button onClick={() => copyLink(inv.token)} className="text-[11px] underline">Zkopírovat odkaz a poslat ručně</button>}</div>
                                                : <span className="text-slate-400">Neodesláno</span>}
                                            {inv.email_sent_at && inv.email_status !== "error" && <div className="text-[10px] text-slate-400">{new Date(inv.email_sent_at).toLocaleString("cs-CZ")}</div>}
                                        </td>
                                        <td className="px-6 py-3 text-right space-x-2 whitespace-nowrap">
                                            {(owner || inv.role === ROLE_TECH) && <>
                                            <button onClick={() => resendInvite(inv.id)} data-testid={`invite-resend-${inv.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-wider border border-orange-500 text-orange-700 hover:bg-orange-50 rounded-sm">
                                                <RefreshCw className="w-3 h-3" /> Znovu odeslat
                                            </button>
                                            <button onClick={() => copyLink(inv.token)} disabled={inv.expired} className="inline-flex items-center gap-1 px-2 py-1 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 disabled:opacity-40 rounded-sm">
                                                <Copy className="w-3 h-3" /> Odkaz
                                            </button>
                                            <button onClick={() => cancelInvite(inv.id)} className="text-slate-400 hover:text-red-600 inline-block align-middle"><X className="w-3.5 h-3.5" /></button>
                                            </>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            </>)}

            {showInvite && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowInvite(false)}>
                    <div className="bg-white border-2 border-slate-900 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-slate-900">
                            <h2 className="font-display text-lg font-bold">Pozvat člena firmy</h2>
                            <button onClick={() => setShowInvite(false)}><X className="w-5 h-5 text-slate-400 hover:text-slate-900" /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="label-eyebrow block mb-1.5">Email *</label>
                                <input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} data-testid="invite-email"
                                       className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm" />
                            </div>
                            <div>
                                <label className="label-eyebrow block mb-1.5">Role</label>
                                <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })} data-testid="invite-role"
                                        className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm">
                                    {!tech && <option value={ROLE_TECH}>Vedoucí technik (jen provoz na stavbě, bez cen)</option>}
                                    <option value="craftsman">Řemeslník (jen vlastní hodiny a kniha jízd)</option>
                                    {owner && <option value="admin">Administrátor (plný provozní přístup)</option>}
                                </select>
                            </div>
                            {inviteForm.role === "craftsman" && (
                                <div>
                                    <label className="label-eyebrow block mb-1.5">Propojit s kartou řemeslníka</label>
                                    <select value={inviteForm.craftsman_id || ""} onChange={(e) => setInviteForm({ ...inviteForm, craftsman_id: e.target.value })} data-testid="invite-craftsman-card"
                                            className="w-full px-3 py-2 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 rounded-sm">
                                        <option value="">— založit novou kartu podle jména —</option>
                                        {cards.map((c) => <option key={c.id} value={c.id}>{c.name}{c.specialization ? ` · ${c.specialization}` : ""}</option>)}
                                    </select>
                                    <div className="text-[11px] text-slate-500 mt-1">Hodiny řemeslníka se zapisují na tuto kartu a propisují do výplat.</div>
                                </div>
                            )}
                            <div className="bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                                Pozvánka se odešle e-mailem (odesílatel podle Nastavení firmy → E-mail). Platí 7 dní; odkaz lze kdykoli zkopírovat a poslat ručně.
                            </div>
                        </div>
                        <div className="flex gap-2 px-6 py-4 bg-slate-50 border-t border-slate-200 justify-end">
                            <button onClick={() => setShowInvite(false)} className="px-4 py-2 text-xs uppercase tracking-wider border border-slate-300 hover:bg-slate-100 rounded-sm">Zrušit</button>
                            <button onClick={invite} data-testid="send-invite" className="px-4 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm">
                                Vytvořit pozvánku
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
